import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { BrandLogo } from '@/components/brand-logo'
import Layout from '@/components/Layout'
import { Spinner } from '@/components/ui/spinner'
import { EmailComposerProvider } from '@/modules/emails/email-composer'
import { WhatsappComposerProvider } from '@/modules/whatsapp/whatsapp-composer'
import { useAuth, RequireRole } from '@/contexts/AuthContext'
import Landing from '@/pages/Landing'
import Login from '@/pages/Login'
import ForgotPassword from '@/pages/ForgotPassword'
import ResetPassword from '@/pages/ResetPassword'

// Authenticated-area pages are lazy-loaded for code-splitting: each route's
// bundle loads on demand instead of at app boot.
const Dashboard = lazy(() => import('@/modules/dashboard/dashboard.page'))
const Calendar = lazy(() => import('@/modules/calendar/calendar.page'))
const Students = lazy(() => import('@/modules/students/students.page'))
const StudentCreate = lazy(() => import('@/modules/students/student-create.page'))
const StudentOnboarding = lazy(() => import('@/modules/students/student-onboarding.page'))
const StudentProfile = lazy(() => import('@/modules/students/student-profile.page'))
const StudentEdit = lazy(() => import('@/modules/students/student-edit.page'))
const Teachers = lazy(() => import('@/modules/teachers/teachers.page'))
const TeacherCreate = lazy(() => import('@/modules/teachers/teacher-create.page'))
const TeacherProfile = lazy(() => import('@/modules/teachers/teacher-profile.page'))
const TeacherEdit = lazy(() => import('@/modules/teachers/teacher-edit.page'))
const ContactCreate = lazy(() => import('@/modules/contacts/contact-create.page'))
const Contacts = lazy(() => import('@/modules/contacts/contacts.page'))
const ContactProfile = lazy(() => import('@/modules/contacts/contact-profile.page'))
const Courses = lazy(() => import('@/modules/courses/courses.page'))
const CourseCreate = lazy(() => import('@/modules/courses/course-create.page'))
const CourseProfile = lazy(() => import('@/modules/courses/course-profile.page'))
const CourseEdit = lazy(() => import('@/modules/courses/course-edit.page'))
const Financial = lazy(() => import('@/modules/financial/financial.page'))
const Invoices = lazy(() => import('@/modules/invoices/invoices.page'))
const InvoiceDetail = lazy(() => import('@/modules/invoices/invoice-detail.page'))
const SriSettings = lazy(() => import('@/modules/sri/sri-settings.page'))
const Products = lazy(() => import('@/modules/products/products.page'))
const PublicInvoice = lazy(() => import('@/modules/invoices/public-invoice.page'))
// Portal del profesor ("Mi aula") — rutas /aula/*, solo para el rol teacher.
const TeacherHours = lazy(() => import('@/modules/teacher-hours/teacher-hours.page'))
const MyCourses = lazy(() => import('@/modules/classroom/my-courses.page'))
const MyCourse = lazy(() => import('@/modules/classroom/my-course.page'))
const MyStudents = lazy(() => import('@/modules/classroom/my-students.page'))
const MyStudent = lazy(() => import('@/modules/classroom/my-student.page'))
const Profile = lazy(() => import('@/pages/Profile'))
const Branches = lazy(() => import('@/modules/branch/branches.page'))
const SelectBranch = lazy(() => import('@/modules/branch/select-branch.page'))
const BranchSettings = lazy(() => import('@/modules/branch/branch-settings.page'))
const Users = lazy(() => import('@/modules/branch/users.page'))
const Docs = lazy(() => import('@/modules/docs/docs.page'))
const Whatsapp = lazy(() => import('@/modules/whatsapp/whatsapp.page'))
const Emails = lazy(() => import('@/modules/emails/emails.page'))
const WhatsappSessions = lazy(() => import('@/modules/whatsapp/whatsapp-sessions.page'))
const WhatsappTemplates = lazy(() => import('@/modules/templates/whatsapp-templates.page'))
const EmailTemplates = lazy(() => import('@/modules/templates/email-templates.page'))
const NotFound = lazy(() => import('@/pages/NotFound'))
const ChangePasswordGate = lazy(() => import('@/pages/ChangePasswordGate'))

function App() {
  const { isAuthenticated, isLoading, user, activeBranchId } = useAuth();
  const location = useLocation();
  // Where "/" (and a stale /login) lands, per role.
  const homePath = user?.role === 'teacher' ? '/aula/cursos' : '/dashboard';

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-muted/30">
        <BrandLogo className="w-56" />
        <Spinner className="size-6 text-primary" />
        <p className="text-sm text-muted-foreground">Cargando sesión…</p>
      </div>
    );
  }

  // If not authenticated, show landing page and auth routes
  if (!isAuthenticated) {
    return (
      // Suspense propio: las páginas de este árbol también son lazy.
      <Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center">
            <Spinner className="size-6 text-primary" />
          </div>
        }
      >
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Comprobante compartido por WhatsApp: se abre sin cuenta. */}
        <Route path="/factura/:token" element={<PublicInvoice />} />
        <Route path="/register" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    );
  }

  // Entró con la clave temporal que le dieron: no ve nada del panel hasta
  // elegir la suya.
  if (user?.mustChangePassword) {
    return (
      <Suspense fallback={null}>
        <ChangePasswordGate />
      </Suspense>
    );
  }

  // Branch-scoped pages cannot load until the user chooses a branch. The
  // selection screen is also reachable later from the sidebar switcher.
  if (!activeBranchId && location.pathname !== '/select-branch') {
    const from = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/select-branch" replace state={{ from }} />;
  }

  if (location.pathname === '/select-branch') {
    return (
      <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><Spinner className="size-6 text-primary" /></div>}>
        <SelectBranch />
      </Suspense>
    );
  }

  // If authenticated, show main app with role-based protection
  return (
    <EmailComposerProvider>
    <WhatsappComposerProvider>
    <Layout>
      <Suspense
        fallback={
          <div className="flex min-h-[60vh] items-center justify-center">
            <Spinner className="size-6 text-primary" />
          </div>
        }
      >
        <Routes>
          {/* Los profesores no tienen panel ni calendario de sucursal: su
              inicio es "Mi aula". */}
          <Route path="/" element={<Navigate to={homePath} replace />} />
        <Route path="/factura/:token" element={<PublicInvoice />} />
        <Route path="/dashboard" element={
          user?.role === 'teacher' ? <Navigate to="/aula/cursos" replace /> : <RequireRole roles={['admin', 'staff']}>
            <Dashboard />
          </RequireRole>
        } />
        <Route path="/calendar" element={
          <RequireRole roles={['admin', 'staff']}>
            <Calendar />
          </RequireRole>
        } />

        <Route path="/aula/horas" element={<RequireRole roles={['teacher']}><TeacherHours /></RequireRole>} />
        <Route path="/teacher-hours" element={<RequireRole roles={['admin']}><TeacherHours /></RequireRole>} />
        {/* Mi aula — portal del profesor */}
        <Route path="/aula" element={<Navigate to="/aula/cursos" replace />} />
        <Route path="/aula/cursos" element={
          <RequireRole roles={['teacher']}>
            <MyCourses />
          </RequireRole>
        } />
        <Route path="/aula/cursos/:id" element={
          <RequireRole roles={['teacher']}>
            <MyCourse />
          </RequireRole>
        } />
        <Route path="/aula/estudiantes" element={
          <RequireRole roles={['teacher']}>
            <MyStudents />
          </RequireRole>
        } />
        <Route path="/aula/estudiantes/:id" element={
          <RequireRole roles={['teacher']}>
            <MyStudent />
          </RequireRole>
        } />

        {/* Profile - all authenticated users */}
        <Route path="/profile" element={<Profile />} />

        {/* Configuración — Admin only */}
        <Route path="/settings/branches" element={
          <RequireRole roles={['admin']}>
            <Branches />
          </RequireRole>
        } />
        <Route path="/settings/users" element={
          <RequireRole roles={['admin']}>
            <Users />
          </RequireRole>
        } />
        <Route path="/settings/branch" element={
          <RequireRole roles={['admin']}>
            <BranchSettings />
          </RequireRole>
        } />
        <Route path="/whatsapp" element={
          <RequireRole roles={['admin', 'staff']}>
            <Whatsapp />
          </RequireRole>
        } />
        <Route path="/emails" element={
          <RequireRole roles={['admin', 'staff']}>
            <Emails />
          </RequireRole>
        } />
        <Route path="/whatsapp-templates" element={
          <RequireRole roles={['admin', 'staff']}>
            <WhatsappTemplates />
          </RequireRole>
        } />
        <Route path="/email-templates" element={
          <RequireRole roles={['admin', 'staff']}>
            <EmailTemplates />
          </RequireRole>
        } />
        <Route path="/whatsapp-sessions" element={
          <RequireRole roles={['admin']}>
            <WhatsappSessions />
          </RequireRole>
        } />

        {/* Student Management - Admin and Staff */}
        <Route path="/students" element={
          <RequireRole roles={['admin', 'staff']}>
            <Students />
          </RequireRole>
        } />
        <Route path="/students/new" element={
          <RequireRole roles={['admin', 'staff']}>
            <StudentCreate />
          </RequireRole>
        } />
        <Route path="/students/:id/inscripcion" element={
          <RequireRole roles={['admin', 'staff']}><StudentOnboarding /></RequireRole>
        } />
        <Route path="/students/:id" element={
          <RequireRole roles={['admin', 'staff']}>
            <StudentProfile />
          </RequireRole>
        } />
        <Route path="/students/:id/edit" element={
          <RequireRole roles={['admin', 'staff']}>
            <StudentEdit />
          </RequireRole>
        } />

        {/* Teacher Management - Admin and Staff */}
        <Route path="/teachers" element={
          <RequireRole roles={['admin', 'staff']}>
            <Teachers />
          </RequireRole>
        } />
        <Route path="/teachers/new" element={
          <RequireRole roles={['admin', 'staff']}>
            <TeacherCreate />
          </RequireRole>
        } />
        <Route path="/teachers/:id" element={
          <RequireRole roles={['admin', 'staff']}>
            <TeacherProfile />
          </RequireRole>
        } />
        <Route path="/teachers/:id/edit" element={
          <RequireRole roles={['admin', 'staff']}>
            <TeacherEdit />
          </RequireRole>
        } />

        <Route path="/contacts" element={<RequireRole roles={['admin', 'staff']}><Contacts /></RequireRole>} />
        <Route path="/contacts/:id" element={<RequireRole roles={['admin', 'staff']}><ContactProfile /></RequireRole>} />
        <Route path="/contacts/new" element={<RequireRole roles={['admin', 'staff']}><ContactCreate /></RequireRole>} />
        {/* Preserve bookmarks while using the single Contacts directory. */}
        <Route path="/guardians" element={
          <RequireRole roles={['admin', 'staff']}>
            <Navigate to="/contacts" replace />
          </RequireRole>
        } />
        <Route path="/guardians/new" element={
          <RequireRole roles={['admin', 'staff']}>
            <Navigate to="/contacts/new" replace />
          </RequireRole>
        } />
        <Route path="/guardians/:id" element={
          <RequireRole roles={['admin', 'staff']}>
            <LegacyContactRedirect />
          </RequireRole>
        } />
        <Route path="/guardians/:id/edit" element={
          <RequireRole roles={['admin', 'staff']}>
            <LegacyContactRedirect />
          </RequireRole>
        } />

        {/* Courses Management - Admin and Staff */}
        <Route path="/courses" element={
          <RequireRole roles={['admin', 'staff']}>
            <Courses />
          </RequireRole>
        } />
        <Route path="/courses/new" element={
          <RequireRole roles={['admin', 'staff']}>
            <CourseCreate />
          </RequireRole>
        } />
        <Route path="/courses/:id" element={
          <RequireRole roles={['admin', 'staff']}>
            <CourseProfile />
          </RequireRole>
        } />
        <Route path="/courses/:id/edit" element={
          <RequireRole roles={['admin', 'staff']}>
            <CourseEdit />
          </RequireRole>
        } />
        {/* Back-compat: the old schedules entry now lives under /courses */}
        <Route path="/schedules" element={<Navigate to="/courses" replace />} />

        {/* Documentation - All authenticated users */}
        <Route path="/docs" element={<Docs />} />
        
        {/* Financial Management - Admin only */}
        <Route path="/financial" element={
          <RequireRole roles={['admin']}>
            <Financial />
          </RequireRole>
        } />
        
        {/* Enlaces antiguos de Cobranzas ahora abren el centro financiero único. */}
        <Route path="/collections" element={<Navigate to="/facturacion" replace />} />
        {/* Inscripciones se gestionan dentro de cada curso */}
        <Route path="/enrollments" element={<Navigate to="/courses" replace />} />

        {/* Facturación electrónica (SRI) — Admin only */}
        <Route path="/facturacion" element={
          <RequireRole roles={['admin']}>
            <Invoices />
          </RequireRole>
        } />
        <Route path="/facturacion/:id" element={
          <RequireRole roles={['admin']}>
            <InvoiceDetail />
          </RequireRole>
        } />
        <Route path="/settings/productos" element={<Navigate to="/products" replace />} />
        <Route path="/products" element={
          <RequireRole roles={['admin']}>
            <Products />
          </RequireRole>
        } />
        <Route path="/settings/facturacion" element={
          <RequireRole roles={['admin']}>
            <SriSettings />
          </RequireRole>
        } />

        {/* Back-compat: el módulo de facturas vivía en /invoices. */}
        <Route path="/invoices/*" element={<Navigate to="/facturacion" replace />} />

        <Route path="/login" element={<Navigate to={homePath} replace />} />
        <Route path="/register" element={<Navigate to={homePath} replace />} />
        <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Layout>
    </WhatsappComposerProvider>
    </EmailComposerProvider>
  )
}

export default App

function LegacyContactRedirect() {
  const { id } = useParams()
  return <Navigate to={`/contacts/${id}`} replace />
}
